import { useState, type ReactNode } from "react";
import { Accordion, AccordionGroup, Card, Flex, LoadingIndicator, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { SPACING } from "~/styles/variables";
import * as styles from "./FormulaComponentAccordionCard_vanilla.css";
import type { FormulaComponentAccordionItem } from "./formulaComponentItems";

type FormulaComponentAccordionCardProps = {
  cardTitle: string;
  items: ReadonlyArray<FormulaComponentAccordionItem>;
  isLoading?: boolean;
  /** PathKeys whose settings in this card failed validation on the last
   * submit; they get an error icon on the header, and the first one is
   * auto-expanded when a submit fails. */
  erroredPathKeys?: ReadonlySet<string>;
  submitCount?: number;
  renderPanel: (pathKey: string) => ReactNode;
};

const NO_ERRORS: ReadonlySet<string> = new Set();

export const FormulaComponentAccordionCard = ({
  cardTitle,
  items,
  isLoading = false,
  erroredPathKeys = NO_ERRORS,
  submitCount = 0,
  renderPanel,
}: FormulaComponentAccordionCardProps) => {
  const [expandedPathKey, setExpandedPathKey] = useState<string | null>(null);
  const cardTestId = `formula-component-card-${cardTitle.toLowerCase().replace(/\s+/g, "-")}`;

  // Derive during render (React docs pattern): collapse the open accordion
  // when its item drops out of the list (e.g. the formula was edited),
  // without an effect that would trigger a cascading render.
  if (expandedPathKey != null && !items.some((item) => item.pathKey === expandedPathKey)) {
    setExpandedPathKey(null);
  }

  // Derive during render: a collapsed accordion unmounts its inputs, so an
  // error inside it would otherwise be unreachable. After each failed
  // submit, expand the first errored item unless an errored one is already
  // open. Formik bumps submitCount a render before it publishes errors, so
  // the submit is only consumed once errored keys are actually visible.
  const [handledSubmitCount, setHandledSubmitCount] = useState(submitCount);
  if (submitCount > handledSubmitCount) {
    const firstErrored = items.find((item) => erroredPathKeys.has(item.pathKey));
    if (firstErrored) {
      setHandledSubmitCount(submitCount);
      if (!(expandedPathKey != null && erroredPathKeys.has(expandedPathKey))) {
        setExpandedPathKey(firstErrored.pathKey);
      }
    }
  }

  if (isLoading) {
    return (
      <Card isFullWidth title={cardTitle} data-testid={cardTestId}>
        <Flex justifyContent="center" className={styles.paddedContainer}>
          <LoadingIndicator />
        </Flex>
      </Card>
    );
  }

  if (items.length === 0) return null;

  return (
    <Card isFullWidth title={cardTitle} data-testid={cardTestId}>
      <AccordionGroup>
        {items.map((item) => (
          <Accordion
            key={item.pathKey}
            title={item.fieldLabel}
            subtitle={
              <>
                <Text variant="secondary-md" asSpan className={styles.subtitleText}>
                  {item.formulaPath.join(" > ")}
                </Text>
                {erroredPathKeys.has(item.pathKey) && (
                  <Icon
                    type="exclamation-circle"
                    color={Colors.Danger600}
                    className={styles.subtitleErrorIcon}
                    aria-label="Contains errors"
                  />
                )}
              </>
            }
            subtitleClass={styles.accordionSubtitle}
            isExpanded={expandedPathKey === item.pathKey}
            onToggle={(nextExpanded) => {
              setExpandedPathKey(nextExpanded ? item.pathKey : null);
            }}
            disableBodyPadding
          >
            <Flex flexDirection="column" gap={SPACING.LARGE} className={styles.paddedContainer}>
              {renderPanel(item.pathKey)}
            </Flex>
          </Accordion>
        ))}
      </AccordionGroup>
    </Card>
  );
};
